import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Search, Calendar, MapPin, TrendingUp, CreditCard, Clock, BarChart2, Award, AlertTriangle, Lightbulb } from 'lucide-react';
import TopContractsTable from './TopContractsTable';
import RecentActivityTimeline from './RecentActivityTimeline';
import DurationDistributionChart from './DurationDistributionChart';
import { CompetitorInsights } from './CompetitorInsights';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { getCpvDescription } from '@/utils/cpvUtils';
import { formatEuro } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SupplierSearch } from './SupplierSearch';
import { useTranslation } from 'react-i18next';
interface CompetitorAnalysisProps {
  defaultTab?: string;
}
export const CompetitorAnalysis = ({
  defaultTab = 'overview'
}: CompetitorAnalysisProps) => {
  const [competitor, setCompetitor] = useState<string>('');
  const [timeframe, setTimeframe] = useState<string>('1year');
  const [cpvCodes, setCpvCodes] = useState<string[]>([]);
  const [minValue, setMinValue] = useState<string>('');
  const [maxValue, setMaxValue] = useState<string>('');
  const [countries, setCountries] = useState<string[]>([]);
  const navigate = useNavigate();
  const {
    t
  } = useTranslation('competitorAnalysis');
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['competitor-analysis', competitor, timeframe, cpvCodes, minValue, maxValue, countries],
    queryFn: async () => {
      if (!competitor.trim()) return null;
      try {
        const {
          data,
          error
        } = await supabase.functions.invoke('analyze-competitors', {
          body: {
            competitor,
            timeframe,
            cpvCodes,
            minValue: minValue ? parseInt(minValue) : undefined,
            maxValue: maxValue ? parseInt(maxValue) : undefined,
            countries
          }
        });
        if (error) {
          console.error('Error fetching competitor data:', error);
          toast({
            title: "Error fetching data",
            description: "There was a problem retrieving competitor information.",
            variant: "destructive"
          });
          throw error;
        }
        if (data && data.contracts) {
          for (const contract of data.contracts) {
            if (contract.cpv) {
              try {
                const description = await getCpvDescription(contract.cpv);
                contract.cpvDescription = description;
              } catch (err) {
                console.error('Error getting CPV description:', err);
                contract.cpvDescription = 'Unknown';
              }
            }
          }
        }
        return data;
      } catch (err) {
        console.error('Error in query function:', err);
        toast({
          title: "Connection error",
          description: "Failed to connect to the server. Please try again.",
          variant: "destructive"
        });
        throw err;
      }
    },
    enabled: competitor.trim().length > 0,
    refetchOnWindowFocus: false,
    retry: 1
  });
  const handleSelectSupplier = (supplierName: string) => {
    setCompetitor(supplierName);
    refetch();
  };
  const handleContractClick = (contractId: string) => {
    navigate(`/tender-view/${contractId}`);
  };
  const emptyStats = {
    totalContracts: 0,
    totalValue: 0,
    averageValue: 0,
    successRate: 0,
    bidsParticipated: 0,
    recentContractsCount: 0
  };
  return <div className="space-y-6 overflow-hidden">
      <SupplierSearch onSelectSupplier={handleSelectSupplier} />

      {isLoading ? <div className="space-y-6">
          <Skeleton className="h-[300px] w-full rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-[200px] w-full rounded-lg" />
            <Skeleton className="h-[200px] w-full rounded-lg" />
            <Skeleton className="h-[200px] w-full rounded-lg" />
          </div>
        </div> : error ? <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('alerts.errorFetchingData')}</AlertTitle>
          <AlertDescription>
            {t('alerts.errorDescription')}
          </AlertDescription>
        </Alert> : !data ? competitor.trim() ? <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('alerts.noDataFoundTitle')}</AlertTitle>
            <AlertDescription>
              {t('alerts.noDataFoundDescription')}
            </AlertDescription>
          </Alert> : null : <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="mb-8">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Award className="h-4 w-4" />
              <span>{t('tabs.overview')}</span>
            </TabsTrigger>
            <TabsTrigger value="financials" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span>{t('tabs.financials')}</span>
            </TabsTrigger>
            <TabsTrigger value="geography" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{t('tabs.geography')}</span>
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              <span>{t('tabs.trends')}</span>
            </TabsTrigger>
            <TabsTrigger value="duration" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{t('tabs.duration')}</span>
            </TabsTrigger>
            
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{t('stats.totalContracts')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.stats?.totalContracts || 0}</div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('stats.recentContractsCount', {
                  count: data.stats?.recentContractsCount || 0
                })}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{t('stats.totalValue')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {formatEuro(data.stats?.totalValue || 0)}
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('stats.avgValue', {
                  value: formatEuro(data.stats?.averageValue || 0)
                })}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{t('stats.successRate')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{data.stats?.successRate || 0}%</div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('stats.bidsParticipated', {
                  count: data.stats?.bidsParticipated || 0
                })}
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.topContracts')}</CardTitle>
                  <CardDescription>
                    {t('cards.topContractsDescription', {
                  competitor
                })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <TopContractsTable contracts={data.topContracts || []} showValueFirst={true} showLocation={true} onRowClick={handleContractClick} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.recentActivity')}</CardTitle>
                  <CardDescription>
                    {t('cards.recentActivityDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RecentActivityTimeline dates={data.activityDates || []} />
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>{t('cards.allContracts')}</CardTitle>
                <CardDescription>
                  {t('cards.allContractsDescription', {
                competitor
              })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TopContractsTable contracts={data.contracts || []} showLocation={true} showDuration={true} showStatus={true} title="" onRowClick={handleContractClick} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="financials" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.valueDistribution')}</CardTitle>
                  <CardDescription>
                    {t('cards.valueDistributionDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  {data.valueDistribution && <DurationDistributionChart data={data.valueDistribution} />}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.topBuyersByValue')}</CardTitle>
                  <CardDescription>
                    {t('cards.topBuyersByValueDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  {data.buyersByValue && <DurationDistributionChart data={data.buyersByValue} />}
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('cards.contractsByValue')}</CardTitle>
                <CardDescription>
                  {t('cards.contractsByValueDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TopContractsTable contracts={data.contracts || []} showValueFirst={true} showLocation={true} onRowClick={handleContractClick} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="geography" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.contractsByCountry')}</CardTitle>
                  <CardDescription>
                    {t('cards.contractsByCountryDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  {data.contractsByCountry && <DurationDistributionChart data={data.contractsByCountry} />}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.valueByCountry')}</CardTitle>
                  <CardDescription>
                    {t('cards.valueByCountryDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  {data.valueByCountry && <DurationDistributionChart data={data.valueByCountry} />}
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('cards.contractListByLocation')}</CardTitle>
                <CardDescription>
                  {t('cards.contractListByLocationDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TopContractsTable contracts={data.contracts ? [...data.contracts].sort((a, b) => {
              const countryA = typeof a.country === 'string' ? a.country : '';
              const countryB = typeof b.country === 'string' ? b.country : '';
              return countryA.localeCompare(countryB);
            }) : []} showLocation={true} showValueFirst={false} onRowClick={handleContractClick} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="trends" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.contractsOverTime')}</CardTitle>
                  <CardDescription>
                    {t('cards.contractsOverTimeDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  {data.contractsByPeriod && <DurationDistributionChart data={data.contractsByPeriod} />}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.valueOverTime')}</CardTitle>
                  <CardDescription>
                    {t('cards.valueOverTimeDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  {data.valueByPeriod && <DurationDistributionChart data={data.valueByPeriod} />}
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('cards.recentContracts')}</CardTitle>
                <CardDescription>
                  {t('cards.recentContractsDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TopContractsTable contracts={data.contracts ? [...data.contracts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) : []} showValueFirst={false} showLocation={true} onRowClick={handleContractClick} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="duration" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.contractDurationDistribution')}</CardTitle>
                  <CardDescription>
                    {t('cards.contractDurationDistributionDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  {data.durationDistribution && <DurationDistributionChart data={data.durationDistribution} />}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>{t('cards.avgDurationByValue')}</CardTitle>
                  <CardDescription>
                    {t('cards.avgDurationByValueDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  {data.durationByValue && <DurationDistributionChart data={data.durationByValue} />}
                </CardContent>
              </Card>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>{t('cards.contractsByDuration')}</CardTitle>
                <CardDescription>
                  {t('cards.contractsByDurationDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TopContractsTable contracts={data.contracts ? [...data.contracts].sort((a, b) => {
              const aDuration = a.duration ? parseInt(a.duration) : 0;
              const bDuration = b.duration ? parseInt(b.duration) : 0;
              return bDuration - aDuration;
            }) : []} showDuration={true} showValueFirst={false} onRowClick={handleContractClick} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ai-insights" className="space-y-6">
            <CompetitorInsights competitor={competitor} competitorData={data} isLoading={isLoading} />
          </TabsContent>
        </Tabs>}
    </div>;
};